import axios from 'axios';

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipGlobalErrorNotice?: boolean;
  }
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
const ROOT_URL = API_BASE_URL.replace(/\/api\/?$/, '');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // C01 — Sanctum SPA : cookies httpOnly envoyés à chaque requête + XSRF auto
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
});

// Récupère le cookie CSRF Sanctum (à appeler avant tout POST/PUT/DELETE non idempotent
// initiant une session : login, register, reset-password)
export const fetchCsrfCookie = () =>
  axios.get(`${ROOT_URL}/sanctum/csrf-cookie`, { withCredentials: true });

// Intercepteur 401 : session expirée → redirection /login.
// On exclut /api/me de la redirection automatique : ce endpoint est appelé
// par AuthContext au mount sur TOUTES les pages (y compris publiques comme
// /forgot-password, /register, /cgv, etc.) pour savoir si l'utilisateur est
// connecté. Un 401 sur /me signifie "pas connecté", c'est un cas normal,
// pas une session expirée — laisser AuthContext gérer (setUser(null)).
const PUBLIC_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/cgu', '/cgv', '/privacy', '/cookies-policy', '/advertising-policy', '/contact', '/help', '/'];

// M09 : protection globale contre les erreurs API non catch localement.
// Throttle pour ne pas spammer si plusieurs composants chargent en même temps.
let lastGlobalNotice = 0;
const NOTICE_THROTTLE_MS = 5000;

const notifyOnce = (msg: string) => {
  const now = Date.now();
  if (now - lastGlobalNotice < NOTICE_THROTTLE_MS) return;
  lastGlobalNotice = now;
  // eslint-disable-next-line no-alert
  window.alert(msg);
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url ?? '';
    const currentPath = window.location.pathname;
    const isMeProbe = requestUrl.endsWith('/me');
    const isOnPublicRoute = PUBLIC_ROUTES.some(
      (route) => currentPath === route || currentPath.startsWith(route + '/')
    );

    if (status === 401) {
      if (!isMeProbe && !isOnPublicRoute) {
        window.location.href = '/login';
      }
    } else if (!error.response) {
      // Erreur réseau (pas de réponse serveur)
      console.error('Erreur réseau API', error.message);
      notifyOnce('Connexion impossible au serveur. Vérifiez votre connexion internet.');
    } else if (status >= 500) {
      console.error(`Erreur serveur ${status}`, requestUrl, error.response?.data);
      if (!error.config?.skipGlobalErrorNotice) {
        notifyOnce('Une erreur serveur est survenue. Veuillez réessayer dans quelques instants.');
      }
    }
    return Promise.reject(error);
  }
);

// M10 : distinction 403 "action non permise" vs "retiré du tableau".
// Un 403 n'a pas un sens unique dans l'app (permission de rôle, quota premium,
// appartenance au tableau...) : ces helpers restent de simples fonctions pures,
// l'interprétation du 403 reste locale à chaque appelant (voir composants).
export const getApiErrorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.message === 'string') return data.message;
  return fallback;
};

// Mapping 429 centralisé (implémentation sans dépendance axios pour la testabilité).
export { getThrottleMessage } from '../utils/throttleMessage';

// Un 403 sur ces endpoints vient toujours de hasPermission($board, 'view'),
// accordé à admin/editor/visitor sans exception côté backend : il signifie donc
// systématiquement "non membre du tableau" (retiré), contrairement aux 403
// d'action (edit/comment/...) qui restent ambigus avec un simple rôle insuffisant.
const BOARD_ACCESS_ENDPOINTS = [/^\/boards\/\d+$/, /^\/boards\/\d+\/members$/, /^\/boards\/\d+\/labels$/];

// 403 émis par OverageService::getBoardLockReason (dépassement non payé) :
// distinct de "retiré du tableau", ne doit jamais déclencher handleBoardAccessDenied.
const BOARD_OVERAGE_ERROR_CODES = ['board_overage_payment_required', 'board_plan_limit_exceeded'];

// 403 émis par OverageService::getCardLockReason (carte hors quota Premium).
const CARD_OVERAGE_ERROR_CODES = ['card_overage_payment_required', 'card_plan_limit_exceeded'];

export const isBoardAccessDenied = (err: any): boolean => {
  if (err?.response?.status !== 403) return false;
  if (BOARD_OVERAGE_ERROR_CODES.indexOf(err?.response?.data?.error) !== -1) return false;
  const url: string = err.config?.url ?? '';
  return BOARD_ACCESS_ENDPOINTS.some((pattern) => pattern.test(url));
};

// 403 "tableau verrouillé par dépassement" (message/redirect préparés côté backend).
export const isBoardOverageLocked = (err: any): boolean => {
  return err?.response?.status === 403
    && BOARD_OVERAGE_ERROR_CODES.indexOf(err?.response?.data?.error) !== -1;
};

// 403 "carte verrouillée par dépassement" (message/redirect préparés côté backend).
export const isCardOverageLocked = (err: any): boolean => {
  return err?.response?.status === 403
    && CARD_OVERAGE_ERROR_CODES.indexOf(err?.response?.data?.error) !== -1;
};

// Comme getApiErrorMessage, mais sans jamais exposer le code technique
// card_overage_payment_required/card_plan_limit_exceeded : toujours le message clair.
export const getCardApiErrorMessage = (err: any, fallback: string): string => {
  if (isCardOverageLocked(err)) {
    return err?.response?.data?.message || fallback;
  }
  return getApiErrorMessage(err, fallback);
};

// Refus d'upload Premium pour stockage insuffisant.
export const isStorageOverageBlocked = (err: any): boolean => {
  return err?.response?.status === 402
    && err?.response?.data?.error === 'storage_overage_payment_required';
};

const BOARD_ACCESS_DENIED_MESSAGE_KEY = 'boardAccessDeniedMessage';

let hasHandledBoardAccessDenied = false;
export const handleBoardAccessDenied = () => {
  if (hasHandledBoardAccessDenied) return;
  hasHandledBoardAccessDenied = true;
  // Le message est conservé pendant la redirection vers le dashboard.
  sessionStorage.setItem(
    BOARD_ACCESS_DENIED_MESSAGE_KEY,
    'Vous n\'avez plus accès à ce tableau : vous avez été retiré par un administrateur.'
  );
  window.location.href = '/boards';
};

// Lecture non destructive pour supporter React.StrictMode.
export const getBoardAccessDeniedMessage = (): string | null =>
  sessionStorage.getItem(BOARD_ACCESS_DENIED_MESSAGE_KEY);

// Suppression après affichage pour éviter une réapparition au refresh.
export const clearBoardAccessDeniedMessage = (): void => {
  sessionStorage.removeItem(BOARD_ACCESS_DENIED_MESSAGE_KEY);
};

// Permet aux catch locaux d'éviter d'afficher leur propre message si le
// rafraîchissement qu'ils viennent de déclencher a déjà révélé une perte
// d'accès complète (et donc déjà affiché handleBoardAccessDenied ci-dessus).
export const wasBoardAccessDenied = (): boolean => hasHandledBoardAccessDenied;

export interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  avatar_url?: string;
  account_type?: 'individual' | 'company';
  company_name?: string;
  email_verified_at?: string;
  created_at: string;
  updated_at: string;
  subscription?: {
    pending_plan_id?: number | null;
    pending_billing_period?: 'monthly' | 'yearly' | null;
  } | null;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  display_name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  has_ads: boolean;
  features: string[];
  max_boards: number | null;
  max_cards_total: number | null;
  max_columns_per_board: number | null;
  max_users: number | null;
  max_storage_mb: number;
  max_guests_per_board: number;
  price_per_10_extra_boards: number;
  price_per_10_extra_boards_yearly: number;
  price_per_1000_extra_cards: number;
  price_per_1000_extra_cards_yearly: number;
  price_per_extra_gb: number;
  price_per_extra_gb_yearly: number;
  price_per_editor_monthly?: number;
  price_per_editor_yearly?: number;
  is_active: boolean;
  is_admin_only: boolean;
  sort_order: number;
}

export interface AdProfilePromptStatus {
  required: boolean;
  mandatory: boolean;
  dismissals: number;
  max_dismissals: number;
}

// Le consentement publicitaire reste facultatif.
export interface AdvertisingProfileData {
  advertising_consent: boolean;
  advertising_interests?: string[];
  pcs_level_1?: string;
  pcs_level_2?: string;
  pcs_level_3?: string;
  pcs_level_4?: string;
  advertising_age?: number;
  advertising_gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
}

export const onboardingApi = {
  getAdProfileStatus: () => api.get<AdProfilePromptStatus>('/onboarding/ad-profile-status'),
  dismissAdProfilePrompt: () =>
    api.post<{ dismissals: number; mandatory: boolean }>('/onboarding/ad-profile/dismiss'),
  completeAdProfile: (data: AdvertisingProfileData) =>
    api.post<{ required: boolean; user: User }>('/onboarding/ad-profile/complete', data),
};

export interface RegisterConsent {
  type: 'cgu' | 'cgv' | 'privacy_policy' | 'cookies_policy' | 'advertising_policy';
  accepted: boolean;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  account_type: 'individual' | 'company';
  plan_id: number;
  billing_period: 'monthly' | 'yearly';
  company_name?: string;
  siret?: string;
  billing_address?: string;
  billing_city?: string;
  billing_postal_code?: string;
  billing_country?: string;
  billing_contact?: string;
  vat_number?: string;
  gdpr_consent?: boolean;
  advertising_consent?: boolean;
  advertising_interests?: string[];
  pcs_level_1?: string;
  pcs_level_2?: string;
  pcs_level_3?: string;
  pcs_level_4?: string;
  advertising_age?: number;
  advertising_gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  consents?: RegisterConsent[];
  oauth_token?: string;
}

export interface BoardMember {
  id: number;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'editor' | 'visitor';
  avatar_url?: string;
}

export interface SearchResult {
  id: number;
  title: string;
  description?: string;
  board_id: number;
  board_title: string;
  board_color?: string;
  column_id: number;
  column_title: string;
  match_in: string;
  archived: boolean;
  updated_at: string;
}

export interface Attachment {
  id: number;
  filename: string;
  mime_type: string;
  size: number;
  formatted_size: string;
  file_type: 'image' | 'video' | 'document' | 'archive' | 'file';
  user: {
    id: number;
    name: string;
  };
  created_at: string;
}

export interface Label {
  id: number;
  card_id: number;
  name: string;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  plan?: SubscriptionPlan;
  message?: string;
}

export interface CardActivity {
  id: number;
  card_id: number;
  user_id?: number;
  user?: User;
  action: string;
  old_value?: string;
  new_value?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export interface Comment {
  id: number;
  card_id: number;
  user_id?: number;
  user?: User;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface Card {
  id: number;
  column_id: number;
  title: string;
  description?: string;
  label_color?: string;
  archived: boolean;
  position: number;
  start_date?: string | null;
  due_date?: string | null;
  created_at: string;
  updated_at: string;
  comments?: Comment[];
  labels?: Label[];
  users?: User[];
  // Une carte hors quota Premium reste toujours retournée (jamais masquée) ;
  // locked/lock_reason indiquent s'il faut régler un dépassement (cf.
  // OverageService::getCardsWithLockStatus).
  locked?: boolean;
  lock_reason?: 'overage_payment_required' | 'plan_limit_exceeded' | null;
  // Informations de quota renvoyées lors de la création d'une carte.
  quota_warning?: boolean;
  quota?: { used: number; limit: number | null; percentage: number | null } | null;
}

export interface Column {
  id: number;
  board_id: number;
  title: string;
  position: number;
  cards: Card[];
  created_at: string;
  updated_at: string;
}

export interface Board {
  id: number;
  title: string;
  user_id?: number;
  background_color?: string;
  columns: Column[];
  members_count?: number;
  owner_name?: string;
  current_user_role?: 'owner' | 'admin' | 'editor' | 'visitor';
  created_at: string;
  updated_at: string;
  // Un tableau hors quota reste toujours retourné (jamais masqué) ; locked/lock_reason
  // indiquent s'il faut régler un dépassement (cf. OverageService::getOwnedBoardsWithLockStatus).
  locked?: boolean;
  lock_reason?: 'overage_payment_required' | 'plan_limit_exceeded' | null;
}

export interface BoardsResponse {
  boards: Board[];
  has_overages: boolean;
  overages: { boards: number; cards: number; storage: number } | null;
}

export const boardApi = {
  getAll: () => api.get<BoardsResponse>('/boards'),
  getOne: (id: number) => api.get<Board>(`/boards/${id}`),
  create: (data: { title: string; background_color?: string }) => api.post<Board>('/boards', data),
  update: (id: number, data: { title?: string; background_color?: string }) => api.put<Board>(`/boards/${id}`, data),
  delete: (id: number) => api.delete(`/boards/${id}`),
};

export const columnApi = {
  getAll: (boardId: number) => api.get<Column[]>(`/boards/${boardId}/columns`),
  create: (boardId: number, data: { title: string; position?: number }) => 
    api.post<Column>(`/boards/${boardId}/columns`, data),
  update: (boardId: number, columnId: number, data: { title?: string; position?: number }) => 
    api.put<Column>(`/boards/${boardId}/columns/${columnId}`, data),
  delete: (boardId: number, columnId: number) => 
    api.delete(`/boards/${boardId}/columns/${columnId}`),
  move: (columnId: number, data: { position: number }) => 
    api.post<Column>(`/columns/${columnId}/move`, data),
};

export const cardApi = {
  getAll: (columnId: number) => api.get<Card[]>(`/columns/${columnId}/cards`),
  create: (columnId: number, data: { title: string; description?: string; position?: number }) => 
    api.post<Card>(`/columns/${columnId}/cards`, data),
  update: (columnId: number, cardId: number, data: { title?: string; description?: string; label_color?: string; archived?: boolean; position?: number; start_date?: string | null; due_date?: string | null; }) => 
    api.put<Card>(`/columns/${columnId}/cards/${cardId}`, data),
  delete: (columnId: number, cardId: number) => 
    api.delete(`/columns/${columnId}/cards/${cardId}`),
  move: (cardId: number, data: { column_id: number; position: number }) => 
    api.post<Card>(`/cards/${cardId}/move`, data),
};

export const commentApi = {
  getAll: (cardId: number) => api.get<Comment[]>(`/cards/${cardId}/comments`),
  create: (cardId: number, data: { content: string }) => 
    api.post<Comment>(`/cards/${cardId}/comments`, data),
  update: (cardId: number, commentId: number, data: { content: string }) => 
    api.put<Comment>(`/cards/${cardId}/comments/${commentId}`, data),
  delete: (cardId: number, commentId: number) => 
    api.delete(`/cards/${cardId}/comments/${commentId}`),
};

export const authApi = {
  register: (data: RegisterData) =>
    api.post<AuthResponse>('/register', data),
  // Méthode simplifiée pour rétrocompatibilité (utilisée par l'ancien composant Register)
  registerSimple: (data: { name: string; email: string; password: string; password_confirmation: string }) =>
    api.post<AuthResponse>('/register', {
      ...data,
      account_type: 'individual' as const,
      plan_id: 1, // Plan gratuit par défaut
      billing_period: 'monthly' as const,
    }),
  login: (data: { email: string; password: string }) =>
    api.post<AuthResponse>('/login', data),
  logout: () => api.post('/logout'),
  me: () => api.get<User>('/me'),
  forgotPassword: (data: { email: string }) =>
    api.post('/forgot-password', data),
  resetPassword: (data: { token: string; email: string; password: string; password_confirmation: string }) =>
    api.post('/reset-password', data),
  verifyEmail: (data: { email: string; token: string }) =>
    api.post<{
      message: string;
      user: User;
      pending_plan_id?: number | null;
      pending_billing_period?: 'monthly' | 'yearly' | null;
    }>('/email/verify', data),
  resendVerification: (data: { email: string }) =>
    api.post('/email/resend', data),
};

export const subscriptionPlanApi = {
  getAll: () => api.get<{ plans: SubscriptionPlan[] }>('/subscription-plans'),
  getAllAdmin: () => api.get<{ plans: SubscriptionPlan[] }>('/admin/subscription-plans'),
  getById: (id: number) => api.get<{ plan: SubscriptionPlan }>(`/subscription-plans/${id}`),
  getByName: (name: string) => api.get<{ plan: SubscriptionPlan }>(`/subscription-plans/name/${name}`),
  create: (data: Omit<SubscriptionPlan, 'id'>) => api.post<{ message: string; plan: SubscriptionPlan }>('/admin/subscription-plans', data),
  update: (id: number, data: Partial<SubscriptionPlan>) => api.put<{ message: string; plan: SubscriptionPlan }>(`/admin/subscription-plans/${id}`, data),
  delete: (id: number) => api.delete<{ message: string }>(`/admin/subscription-plans/${id}`),
  assignGoldenTicket: (userId: number) => api.post(`/admin/users/${userId}/assign-golden-ticket`, {}),
  revokeGoldenTicket: (userId: number) => api.post(`/admin/users/${userId}/revoke-golden-ticket`, {}),
  deleteAdvertisingData: (userId: number) => api.delete(`/admin/users/${userId}/advertising-data`),
};

export const paymentApi = {
  getPublicKey: () => api.get<{ public_key: string; mode: string }>('/payment/public-key'),
  createPayment: (data: { amount: number; currency: string; card_token?: string; description?: string }) =>
    api.post('/payment/create', data),
  // C04 — Stancer hosted : Ventilio délègue la saisie carte à la page hostée de Stancer.
  hostedCreate: (data: {
    amount: number;
    currency?: string;
    description?: string;
    plan_id: number;
    billing_period: 'monthly' | 'yearly';
  }) =>
    api.post<{ payment_id: string; payment_page_url: string; amount: number; currency: string }>(
      '/payment/hosted-create',
      data,
      { skipGlobalErrorNotice: true }
    ),
  getPaymentStatus: (paymentId: string) => api.get(`/payment/${paymentId}`),
  getLastPendingId: () => api.get<{ payment_id: string }>('/payment/last-pending'),
  refundPayment: (paymentId: string, amount?: number) =>
    api.post(`/payment/${paymentId}/refund`, { amount }),
};

export const activityApi = {
  getAll: (cardId: number) => api.get<CardActivity[]>(`/cards/${cardId}/activities`),
};

export interface PendingBoardInvitation {
  id: number;
  email: string;
  role: string;
  invited_by: string | null;
  created_at: string;
}

export const boardMemberApi = {
  getAll: (boardId: number) => api.get<BoardMember[]>(`/boards/${boardId}/members`),
  invite: (boardId: number, data: { email: string; role: string }) =>
    api.post<BoardMember>(`/boards/${boardId}/members/invite`, data),
  getInvitations: (boardId: number) => api.get<PendingBoardInvitation[]>(`/boards/${boardId}/invitations`),
  cancelInvitation: (boardId: number, invitationId: number) =>
    api.delete(`/boards/${boardId}/invitations/${invitationId}`),
  updateRole: (boardId: number, userId: number, role: string) => 
    api.put(`/boards/${boardId}/members/${userId}/role`, { role }),
  remove: (boardId: number, userId: number) => 
    api.delete(`/boards/${boardId}/members/${userId}`),
  generateInviteLink: (boardId: number, role: string) => 
    api.post<{ invite_url: string; invite_token: string; invite_role: string }>(`/boards/${boardId}/invite-link`, { role }),
  disableInviteLink: (boardId: number) => 
    api.delete(`/boards/${boardId}/invite-link`),
  acceptInviteLink: (token: string) => 
    api.post<{ message: string; board: Board; role: string }>(`/join/${token}`),
};

export const searchApi = {
  searchCards: (query: string, includeArchived: boolean = false, boardId?: number) =>
    api.get<{ query: string; count: number; results: SearchResult[] }>(
      `/search/cards?query=${encodeURIComponent(query)}&include_archived=${includeArchived}${boardId ? `&board_id=${boardId}` : ''}`
    ),
};

export const subscriptionApi = {
  getCurrent: () => api.get('/subscription'),
  changePlan: (planId: number, billingPeriod: 'monthly' | 'yearly') =>
    api.post('/subscription/change-plan', { plan_id: planId, billing_period: billingPeriod }),
  cancel: () => api.post('/subscription/cancel'),
  reactivate: () => api.post('/subscription/reactivate'),
  cancelPendingPlan: () => api.post('/subscription/cancel-pending-plan'),
  getUsage: () => api.get('/subscription/usage'),
};

// Vendu par tranches de 10 tableaux, jamais un nombre libre.
export const BOARDS_PER_TRANCHE = 10;

export interface BoardOverageStatus {
  can_purchase: boolean;
  plan_included_boards: number | null;
  effective_limit: number | null;
  boards_per_tranche: number;
  active_overage_tranches: number;
  active_overage_quantity: number;
  active_overages: Array<{
    id: number;
    tranches: number;
    boards: number;
    period: 'monthly' | 'yearly';
    starts_at: string;
    expires_at: string;
  }>;
  pricing: {
    price_per_tranche_monthly: number;
    price_per_tranche_yearly: number;
  };
}

export const boardOverageApi = {
  getStatus: () => api.get<BoardOverageStatus>('/board-overages'),
  // `tranches` = nombre de tranches de 10, jamais un nombre de tableaux direct.
  checkout: (data: { tranches: number }) =>
    api.post<{ payment_id: string; payment_page_url: string; amount: number; currency: string }>(
      '/board-overages/checkout',
      data
    ),
  confirm: (paymentId: string) =>
    api.post<{ payment_status: string; processed: boolean; effective_limit: number | null }>(
      `/board-overages/${paymentId}/confirm`
    ),
};

// Vendu par blocs de 1000 cartes, jamais un nombre libre.
export const CARDS_PER_TRANCHE = 1000;

export interface CardOverageBoardStatus {
  board_id: number;
  board_title: string;
  cards_count: number;
}

export interface CardOverageStatus {
  can_purchase: boolean;
  cards_per_tranche: number;
  plan_included_cards: number | null;
  effective_limit: number | null;
  total_used: number;
  active_overage_tranches: number;
  active_overage_quantity: number;
  active_overages: Array<{
    id: number;
    tranches: number;
    cards: number;
    period: 'monthly' | 'yearly';
    starts_at: string;
    expires_at: string;
  }>;
  boards: CardOverageBoardStatus[];
  pricing: {
    price_per_tranche_monthly: number;
    price_per_tranche_yearly: number;
  };
}

export const cardOverageApi = {
  getStatus: () => api.get<CardOverageStatus>('/card-overages'),
  // `tranches` = nombre de blocs de 1000, jamais un nombre de cartes direct.
  checkout: (data: { tranches: number }) =>
    api.post<{ payment_id: string; payment_page_url: string; amount: number; currency: string }>(
      '/card-overages/checkout',
      data
    ),
  confirm: (paymentId: string) =>
    api.post<{ payment_status: string; processed: boolean; effective_limit: number | null }>(
      `/card-overages/${paymentId}/confirm`
    ),
};

export interface StorageOverageStatus {
  can_purchase: boolean;
  included_limit_mb: number | null;
  effective_limit_mb: number | null;
  active_overage_gb: number;
  active_overages: Array<{
    id: number;
    gb: number;
    period: 'monthly' | 'yearly';
    starts_at: string;
    expires_at: string;
  }>;
  pricing: {
    price_per_gb_monthly: number;
    price_per_gb_yearly: number;
  };
}

export const storageOverageApi = {
  getStatus: () => api.get<StorageOverageStatus>('/storage-overages'),
  checkout: (data: { gb: number }) =>
    api.post<{ payment_id: string; payment_page_url: string; amount: number; currency: string }>(
      '/storage-overages/checkout',
      data
    ),
  confirm: (paymentId: string) =>
    api.post<{ payment_status: string; processed: boolean; effective_limit_mb: number | null }>(
      `/storage-overages/${paymentId}/confirm`
    ),
};

export const documentApi = {
  getAll: (params?: { sort_by?: string; sort_order?: string; filter_type?: string }) => 
    api.get('/documents', { params }),
  delete: (id: number) => api.delete(`/documents/${id}`),
  deleteMultiple: (documentIds: number[]) => 
    api.post('/documents/delete-multiple', { document_ids: documentIds }),
};

export const attachmentApi = {
  getAll: (cardId: number) => api.get<Attachment[]>(`/cards/${cardId}/attachments`),
  upload: (cardId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<Attachment>(`/cards/${cardId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  download: (cardId: number, attachmentId: number) => 
    api.get(`/cards/${cardId}/attachments/${attachmentId}/download`, { responseType: 'blob' }),
  delete: (cardId: number, attachmentId: number) => 
    api.delete(`/cards/${cardId}/attachments/${attachmentId}`),
};

export const labelApi = {
  getBoardLabels: (boardId: number) => api.get<Array<{ name: string; color: string }>>(`/boards/${boardId}/labels`),
  getAll: (cardId: number) => api.get<Label[]>(`/cards/${cardId}/labels`),
  create: (cardId: number, data: { name: string; color: string }) =>
    api.post<Label>(`/cards/${cardId}/labels`, data),
  update: (cardId: number, labelId: number, data: { name?: string; color?: string }) =>
    api.put<Label>(`/cards/${cardId}/labels/${labelId}`, data),
  delete: (cardId: number, labelId: number) =>
    api.delete(`/cards/${cardId}/labels/${labelId}`),
  deleteFromBoard: (boardId: number, data: { name: string; color: string }) =>
    api.delete(`/boards/${boardId}/labels`, { data }),
};

export const assignmentApi = {
  getAll: (cardId: number) => api.get<User[]>(`/cards/${cardId}/assignments`),
  assign: (cardId: number, userId: number) => 
    api.post<User>(`/cards/${cardId}/assignments`, { user_id: userId }),
  unassign: (cardId: number, userId: number) => 
    api.delete(`/cards/${cardId}/assignments/${userId}`),
};

export interface LegalDocument {
  id: number;
  type: string;
  version: string;
  content: string;
  is_active: boolean;
  effective_date: string | null;
}

export const legalApi = {
  getDocument: (type: string) => api.get<LegalDocument>(`/legal/${type}`),
};

export interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  // Honeypot anti-spam
  website?: string;
}

export const contactApi = {
  send: (data: ContactFormData) => api.post<{ message: string }>('/contact', data),
};

export const consentApi = {
  store: (consents: Array<{ legal_document_id: number; accepted: boolean }>) =>
    api.post('/consents', { consents }),
  syncCookies: (accepted: boolean) =>
    api.post('/consents/sync-cookies', { accepted }),
};

export const profileApi = {
  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post<{ message: string; avatar_url: string }>('/profile/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteAvatar: () => api.delete('/profile/avatar'),
};

export type SupportTicketCategory = 'technical' | 'billing' | 'feature_request' | 'bug' | 'other';
export type SupportTicketStatus = 'open' | 'in_progress' | 'waiting_user' | 'resolved' | 'closed';
export type SupportTicketPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface SupportTicketMessage {
  id: number;
  ticket_id: number;
  user_id: number;
  message: string;
  is_admin: boolean;
  created_at: string;
  user?: { id: number; name: string };
}

export interface SupportTicket {
  id: number;
  subject: string;
  message: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  status: SupportTicketStatus;
  created_at: string;
  messages?: SupportTicketMessage[];
}

export const supportApi = {
  getAll: () => api.get<{ data: SupportTicket[] }>('/support-tickets'),
  get: (id: number) => api.get<SupportTicket>(`/support-tickets/${id}`),
  create: (data: { subject: string; category: SupportTicketCategory; message: string }) =>
    api.post<SupportTicket>('/support-tickets', data),
  reply: (id: number, message: string) =>
    api.post<SupportTicketMessage>(`/support-tickets/${id}/messages`, { message }),
};

export default api;